import { Box, Button, HStack, Text, Textarea, VStack } from '@chakra-ui/react';
import { ThumbRatingButton } from 'components/common/ThumbRatingButton';
import * as Sentry from '@sentry/react';
import {
  useSessionPlanFeedback,
  useSubmitSessionPlanFeedback,
} from 'hooks/api/backend/session-plan';
import { ReactElement, useEffect, useState } from 'react';
import { FeedbackRating } from 'types/Feedback';

type Props = {
  planId: string;
};

export const SessionPlanFeedbackCard = ({ planId }: Props): ReactElement | null => {
  const { data: existing, isLoading } = useSessionPlanFeedback(planId);
  const submitMutation = useSubmitSessionPlanFeedback();
  const [rating, setRating] = useState<FeedbackRating | null>(null);
  const [comment, setComment] = useState('');
  const [isSaved, setIsSaved] = useState(false);

  useEffect(() => {
    if (!existing) return;
    setRating(existing.rating);
    setComment(existing.comment ?? '');
    setIsSaved(true);
  }, [existing]);

  if (isLoading) return null;

  const handleSubmit = async (): Promise<void> => {
    if (rating === null) return;
    try {
      await submitMutation.mutateAsync({ planId, rating, comment: comment.trim() });
      setIsSaved(true);
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const select = (value: FeedbackRating): void => {
    setRating(value);
    setIsSaved(false);
  };

  return (
    <Box
      bg="background.secondary"
      borderRadius="md"
      p="m"
      mt="l"
      border="1px solid"
      borderColor="border.accent"
    >
      <VStack align="stretch" gap="s">
        <Text textStyle="body.m.medium" color="text.primary">
          Wie hilfreich ist diese Unterrichtsplanung?
        </Text>
        <Text textStyle="body.s.regular" color="text.secondary">
          Dein Feedback hilft uns, die Funktion in der Beta zu verbessern.
        </Text>
        <HStack gap="s">
          <ThumbRatingButton
            emoji="👍"
            ariaLabel="Hilfreich"
            isSelected={rating === FeedbackRating.HELPFUL}
            onClick={() => select(FeedbackRating.HELPFUL)}
          />
          <ThumbRatingButton
            emoji="👎"
            ariaLabel="Nicht hilfreich"
            isSelected={rating === FeedbackRating.NOT_HELPFUL}
            onClick={() => select(FeedbackRating.NOT_HELPFUL)}
          />
        </HStack>
        {rating !== null && (
          <>
            <Textarea
              value={comment}
              maxLength={2000}
              placeholder="Was war gut, was hat gefehlt oder war falsch? (optional)"
              onChange={(event) => {
                setComment(event.target.value);
                setIsSaved(false);
              }}
            />
            <HStack gap="s">
              <Button
                variant="primary"
                loading={submitMutation.isPending}
                disabled={isSaved}
                onClick={handleSubmit}
              >
                Feedback senden
              </Button>
              {isSaved && (
                <Text textStyle="body.s.regular" color="text.secondary">
                  Danke für dein Feedback!
                </Text>
              )}
              {submitMutation.isError && (
                <Text textStyle="body.s.regular" color="text.error">
                  Das Feedback konnte nicht gesendet werden.
                </Text>
              )}
            </HStack>
          </>
        )}
      </VStack>
    </Box>
  );
};
